import {
  useEffect,
  useState,
} from 'react'
import axios from 'axios'
import { clearAllCache } from '../../Database/Database.services/db'

const useAuthenticationContext = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Production login
  const userLogin = async (email, password, rememberMe) => {
    try {
      const {
        data: {
          accessToken = '',
          refreshToken = '',
          user: newUser = {},
        } = {},
      } = await axios.post('/userLogin', { email, password })

      sessionStorage.setItem('accessToken', accessToken)
      if (rememberMe) localStorage.setItem('refreshToken', refreshToken)

      // Store user data for session restoration (like impersonation does)
      sessionStorage.setItem('userData', JSON.stringify(newUser))

      setIsAuthenticated(true)
      setUser(newUser)

      return { success: true }
    } catch (error) {
      console.error('Login failed:', error)
      return { success: false, error: error.message }
    }
  }

  // Backend impersonation (production feature)
  const startImpersonation = async (targetUserId, reason = 'Administrative access') => {
    try {
      const adminToken = sessionStorage.getItem('accessToken')
      if (!adminToken) {
        throw new Error('No admin token found')
      }

      // Save the admin token for potential user switching
      if (!getImpersonationStatus()) {
        sessionStorage.setItem('originalAdminToken', adminToken)
      }

      console.log('🎭 IMP: [FRONTEND] BEFORE START:')
      console.log('🎭 IMP: Current token:', adminToken ? `${adminToken.substring(0, 50)}...` : 'none')
      console.log(`🎭 IMP: Starting impersonation for user: ${targetUserId}`)

      const response = await axios.post('/userAuthentication/userImpersonate', {
        targetUserId,
        reason,
      }, {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Impersonation failed')
      }

      const { accessToken, refreshToken, impersonation } = response.data

      console.log('🎭 IMP: [FRONTEND] RECEIVED RESPONSE:')
      console.log('🎭 IMP: data.accessToken (50 chars):', accessToken ? `${accessToken.substring(0, 50)}...` : 'none')
      console.log('🎭 IMP: data.impersonation:', impersonation)

      // Décoder le token reçu pour vérifier sa structure
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]))
          console.log('🎭 IMP: [FRONTEND] RECEIVED TOKEN PAYLOAD:')
          console.log('🎭 IMP: payload.email:', payload.email)
          console.log('🎭 IMP: payload.role:', payload.role)
          console.log('🎭 IMP: payload.impersonated_user:', payload.impersonated_user)
          console.log('🎭 IMP: payload.impersonation:', payload.impersonation)

          if (payload.impersonated_user) {
            console.log('🎭 IMP: ✅ [FRONTEND] Received valid impersonation token')
          } else {
            console.log('🎭 IMP: ❌ [FRONTEND] Received standard token - BACKEND BUG!')
          }
        } catch (e) {
          console.error('🎭 IMP: ❌ [FRONTEND] Error decoding received token:', e)
        }
      }

      console.log('🎭 IMP: [FRONTEND] STORING TOKEN:')
      // Store new tokens and impersonation data (following codebase convention)
      sessionStorage.setItem('accessToken', accessToken)
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      sessionStorage.setItem('impersonationData', JSON.stringify(impersonation))

      console.log('🎭 IMP: [FRONTEND] AFTER STORAGE:')
      const storedToken = sessionStorage.getItem('accessToken')
      console.log('🎭 IMP: Stored token:', storedToken ? `${storedToken.substring(0, 50)}...` : 'none')
      console.log(`🎭 IMP: ✅ Successfully impersonating: ${impersonation.target_user.email}`)

      // Reload to apply new context
      window.location.reload()

      return { success: true, data: response.data }
    } catch (error) {
      console.error('🎭 IMP: ❌ Impersonation failed:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Impersonation failed'
      return { success: false, error: errorMessage }
    }
  }

  const stopImpersonation = async () => {
    try {
      console.log('🎭 IMP: [FRONTEND] BEFORE STOP:')

      const impersonationToken = sessionStorage.getItem('accessToken')
      if (!impersonationToken) {
        throw new Error('No impersonation token found')
      }

      console.log('🎭 IMP: Token to send:', impersonationToken ? `${impersonationToken.substring(0, 50)}...` : 'none')
      console.log('🎭 IMP: Impersonation data:', sessionStorage.getItem('impersonationData'))

      // Décoder le token qu'on va envoyer - TEST CRITIQUE
      if (impersonationToken) {
        try {
          const payload = JSON.parse(atob(impersonationToken.split('.')[1]))
          console.log('🎭 IMP: [FRONTEND] TOKEN TO SEND PAYLOAD:')
          console.log('🎭 IMP: payload.email:', payload.email)
          console.log('🎭 IMP: payload.role:', payload.role)
          console.log('🎭 IMP: payload.impersonated_user exists:', !!payload.impersonated_user)
          console.log('🎭 IMP: payload.impersonation exists:', !!payload.impersonation)

          if (payload.impersonated_user) {
            console.log('🎭 IMP: ✅ [FRONTEND] Sending impersonation token - CORRECT')
          } else {
            console.log('🎭 IMP: ❌ [FRONTEND] Sending standard token - BUG!')
            console.log('🎭 IMP: ❌ This will cause 403 Forbidden error')
            console.log('🎭 IMP: 🚨 PROBLÈME: Token d\'impersonation perdu quelque part!')
          }
        } catch (e) {
          console.error('🎭 IMP: ❌ [FRONTEND] Error decoding token to send:', e)
        }
      }

      console.log('🎭 IMP: 🔄 Stopping impersonation...')

      const response = await axios.post('/userAuthentication/userImpersonateStop', {}, {
        headers: {
          'Authorization': `Bearer ${impersonationToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to stop impersonation')
      }

      const { accessToken, refreshToken, adminUser } = response.data

      console.log('🎭 IMP: RECEIVED - Admin token restored:', accessToken ? `${accessToken.substring(0, 50)}...` : 'none')

      // Restore admin tokens and clean up
      sessionStorage.setItem('accessToken', accessToken)
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      sessionStorage.removeItem('impersonationData')
      sessionStorage.removeItem('originalAdminToken')

      const restoredToken = sessionStorage.getItem('accessToken')
      console.log('🎭 IMP: AFTER RESTORATION - Token in sessionStorage:', restoredToken ? `${restoredToken.substring(0, 50)}...` : 'none')
      console.log(`🎭 IMP: ✅ Stopped impersonation, returned to: ${adminUser.email}`)

      // Reload to apply admin context
      window.location.reload()

      return { success: true, data: response.data }
    } catch (error) {
      console.error('🎭 IMP: ❌ Failed to stop impersonation:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to stop impersonation'
      return { success: false, error: errorMessage }
    }
  }

  const getImpersonationStatus = () => {
    const impersonationData = sessionStorage.getItem('impersonationData')
    return impersonationData ? JSON.parse(impersonationData) : null
  }

  // Switch impersonation directly from one user to another
  const switchImpersonation = async (targetUserId, reason = 'Administrative access') => {
    try {
      const currentImpersonation = getImpersonationStatus()
      if (!currentImpersonation) {
        // No current impersonation, just start normally
        return await startImpersonation(targetUserId, reason)
      }

      console.log('🔄 SWITCH: Attempting to switch impersonation from', currentImpersonation.target_user.email, 'to user ID', targetUserId)

      // Get the original admin token that was saved when impersonation started
      const originalAdminToken = sessionStorage.getItem('originalAdminToken')
      
      if (!originalAdminToken) {
        console.error('🔄 SWITCH: No original admin token found, falling back to stop+start with reload')
        // Fallback to regular stop which will cause a reload
        return await stopImpersonation()
      }

      console.log('🔄 SWITCH: Found original admin token, proceeding with direct switch')

      // Stop the current impersonation first by calling the backend directly
      const stopResponse = await axios.post('/userAuthentication/userImpersonateStop', {}, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      })

      if (!stopResponse.data.success) {
        throw new Error('Failed to stop current impersonation')
      }

      console.log('🔄 SWITCH: Current impersonation stopped, starting new one')

      // Now start the new impersonation using the original admin token
      const startResponse = await axios.post('/userAuthentication/userImpersonate', {
        targetUserId,
        reason,
      }, {
        headers: {
          'Authorization': `Bearer ${originalAdminToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (!startResponse.data.success) {
        throw new Error('Failed to start new impersonation')
      }

      const { accessToken, refreshToken, impersonation } = startResponse.data

      console.log('🎭 SWITCH: Successfully switched to:', impersonation.target_user.email)

      // Update tokens and impersonation data (keep the original admin token for further switches)
      sessionStorage.setItem('accessToken', accessToken)
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      sessionStorage.setItem('impersonationData', JSON.stringify(impersonation))
      // Keep originalAdminToken for potential future switches

      // Update user state without page reload
      await userCheckSession()

      return { success: true, data: startResponse.data }
    } catch (error) {
      console.error('🎭 SWITCH: ❌ Failed to switch impersonation:', error)
      console.log('🎭 SWITCH: Fallback to normal stop (will cause page reload)')
      
      // Fallback to normal stop which will reload the page
      return await stopImpersonation()
    }
  }

  // Get users for impersonation (backend version)
  const getAvailableUsersFromBackend = async () => {
    try {
      const response = await axios.post('/getCollection', {
        collectionName: 'users',
      })

      const { collection = [] } = response.data
      return collection
    } catch (error) {
      console.error('❌ Failed to fetch users from backend:', error)
      return []
    }
  }

  const userLogout = async () => {
    // Clear ALL authentication tokens and session data (comprehensive cleanup)
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('userData') // Données utilisateur stockées
    sessionStorage.removeItem('impersonationData') // Données d'impersonification backend
    sessionStorage.removeItem('impersonatedUser') // Impersonification legacy
    sessionStorage.removeItem('impersonatedAttributes') // Attributs SuperAdmin
    localStorage.removeItem('refreshToken')

    // Clear all cached data from IndexedDB
    try {
      await clearAllCache()
      console.log('🗑️ Cache cleared on logout')
    } catch (error) {
      console.error('❌ Error clearing cache on logout:', error)
    }

    // Update authentication state
    setIsAuthenticated(false)
    setUser(null)

    console.log('👋 User logged out successfully - all session data cleared')
  }

  const userCheckSession = async () => {
    setIsLoading(true)

    try {
      // Check for backend impersonation session first
      const impersonationData = sessionStorage.getItem('impersonationData')
      if (impersonationData) {
        try {
          const impersonationInfo = JSON.parse(impersonationData)
          console.log(`🔄 Restoring backend impersonation session:`, impersonationInfo.target_user.email)

          // Use target user info from backend impersonation
          setIsAuthenticated(true)
          setUser(impersonationInfo.target_user)
          setIsLoading(false)
          return
        } catch (error) {
          console.error('❌ Failed to parse impersonation data:', error)
          sessionStorage.removeItem('impersonationData')
        }
      }

      // Check real session
      const accessToken = sessionStorage.getItem('accessToken')
      if (accessToken) {
        // Récupérer les données utilisateur stockées lors du login (comme l'impersonation)
        const userData = sessionStorage.getItem('userData')
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData)
            console.log(`🔄 Restoring user session:`, parsedUser.email)

            setIsAuthenticated(true)
            setUser(parsedUser)
            setIsLoading(false)
            return
          } catch (error) {
            console.error('❌ Failed to parse user data:', error)
            sessionStorage.removeItem('userData')
          }
        }

        // Si pas de userData, garder le comportement actuel
        setIsAuthenticated(true)
        setIsLoading(false)
        return
      }

      // Try refresh token
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        // No tokens found, user is not authenticated
        console.log('🔒 No authentication tokens found, user must login')
        setIsAuthenticated(false)
        setUser(null)
        setIsLoading(false)
        return
      }

      try {
        const {
          data: {
            accessToken: newAccessToken = '',
            user: refreshedUser = {},
          } = {},
        } = await axios.post('/userRefreshAccessToken', { refreshToken })

        sessionStorage.setItem('accessToken', newAccessToken)
        // Update stored user data with fresh data from refresh token
        sessionStorage.setItem('userData', JSON.stringify(refreshedUser))

        setIsAuthenticated(true)
        setUser(refreshedUser)
      } catch {
        userLogout()
      }
    } catch (error) {
      console.error('Session check failed:', error)
      userLogout()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    userCheckSession()
  }, []) // eslint-disable-line

  return {
    // Core authentication
    userLogin,
    userLogout,
    userCheckSession,
    isAuthenticated,
    isLoading,
    user,

    // Backend impersonation (production)
    startImpersonation,
    stopImpersonation,
    switchImpersonation,
    getImpersonationStatus,
    getAvailableUsersFromBackend,

    // Legacy support
    isDevelopment: process.env.NODE_ENV === 'development',
  }
}

export default useAuthenticationContext
